import { useProjectParams } from '@hooks/useProjectParams';
import { getErrorHandlingState } from 'app/selectors';
import { ChooseSectionText } from 'components/ChooseSectionText';
import { SelectVersionsWorkOrder } from 'components/SelectVersionsWorkOrder';
import { Toolbar } from 'components/Toolbar';
import { TooltipWrapper } from 'components/TooltipWrapper';
import {
    findAllConstructionTypesFromObjects,
    WorkOrderConstructionTypesFromObjects,
} from 'components/WorkOrderConstructionTypesTabSelect';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type {
    BimActKc2OtherData,
    BimActKc2OtherItemData,
    BimChapterEnum,
    BimPriceLevelEnum,
    BimWorkOrderObjectData,
    ConstructionTypeData,
    WorkOrderChangeObjectData,
} from 'gql/graphql';
import { BimWorkOrderFormationStageEnum, ContractStatusEnum, SectionEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { usePreviousValue } from 'hooks/usePrevious';
import { BimPriceLevelChoice } from 'pages/Project/BimProject/BimCommonComponents/BimPriceLevelChoice';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import type { IListItem } from 'types/Contract';
import { Button, IconArrowDown, Layout, Popover } from 'uikit';
import { isNumber } from 'utils';

import type React from 'react';
import type { ElementRef } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { bimWorkOrderVersionsGql } from '../../WorkOrderVersion/workOrderVersionQueries';
import { BimActKc2OtherEdit } from '../BimActKc2OtherEdit/BimActKc2OtherEdit';
import { BimAddOrderOther } from '../BimAddOrderOther';
import { BimAddKC2 } from '../BimAddOrEditKC2';
import { BimEditActKC2OtherCorrection } from '../BimEditActKC2OtherCorrection/BimEditActKC2OtherCorrection';
import { BimOrderRequisites } from '../BimOrderRequisites';
import { BimUploadPriceFiles } from '../BimUploadPriceFiles/BimUploadPriceFiles';
import { BimWorkOrderFilterWithTools } from '../BimWorkOrderFilter';
import { BimWorkOrderKebabMenu } from '../BimWorkOrderKebabMenu';
import { BimWorkOrderOthersTable } from '../BimWorkOrderOthersPrice';
import { useBimActsKC2Other } from '../BimWorkOrderOthersPrice/hooks/useBimActsKC2Other';
import { BimWorkOrderPriceCounter } from '../BimWorkOrderPriceCounter';
import { BimWorkOrderPriceTable } from '../BimWorkOrderPriceTable';
import { useBimWorkOrderPriceCounter } from '../BimWorkOrderPriceTable/hooks';
import { BimWorkOrderTotalsTableContainer } from '../BimWorkOrderTotalsTableContainer';
import { useBimWorkOrderObjects, useBimWorkOrderTotalsRow, useGetBimWorkOrderDataFromProject } from '../hooks';
import {
    BimWorkOrderRowTypeEnum,
    isBimChapter,
    isOtherBimChapter,
    isTotalsBimChapter,
    totalsByConstructionTypeKey,
} from '../types';

import { useBimScrollToAct } from './useBimScrollToAct';

import './bimWorkOrderTable.css';

export const BimWorkOrder = () => {
    const { id: projectId, orderId, methodology } = useProjectParams();
    const prevOrderId = usePreviousValue(orderId);
    const { requestData, requestError } = useSelector(getErrorHandlingState);

    const {
        workOrderTitle,
        workOrderStatus,
        hasOtherActsKc2,
        hasInProgress,
        hasActsKC2,
        isWorkOrderChangeRequired,
        isFrameContract,
        projectDataStatus,
        refreshProject,
        projectData,
        hasWorkOrderChangesValidStatus,
    } = useGetBimWorkOrderDataFromProject({});

    const {
        orderBim: orderRules,
        aktKC2BimOther: aktKC2BimOtherRules,
        aktKC2Bim: aktKC2BimRules,
    } = useAccess({
        workOrderStatus,
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state }: { state: { openEditRequisites?: boolean; actNavInfo?: any } } = useLocation();
    const workOrderId = Number(orderId);
    const [isPopoverVisible, setIsPopoverVisible] = useState(false);
    const buttonAddedRef = useRef(null);

    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);

    const statusProject = projectData?.status;

    const contractStatus = projectData?.contractNavigation?.data?.contractNavData?.status;

    const currentFrameWorkOrder = useMemo(
        () => projectData?.contractNavigation?.data?.workOrderNavs?.find((nav) => nav.id === workOrderId),
        [projectData?.contractNavigation?.data?.workOrderNavs, workOrderId],
    );

    const isPageDataLoading = projectDataStatus === RequestStatus.FETCHING;

    const isObjectsFormationStage = useMemo(
        () => currentFrameWorkOrder?.metadata?.formationStage === BimWorkOrderFormationStageEnum.ObjectsFormation,
        [currentFrameWorkOrder?.metadata],
    );

    const priceTableApiRef = useRef<ElementRef<typeof BimWorkOrderPriceTable>>(null);
    const requisitesRefBtn = useRef<ElementRef<typeof BimOrderRequisites>>(null);
    const workOrderFilterRef = useRef<ElementRef<typeof BimWorkOrderFilterWithTools>>(null);
    const resetCheckedRowsRef = useRef<() => void>();

    const [workOrderRootObject, setWorkOrderRootObject] = useState<IListItem<number>>();
    const [workOrderSectionOrChapter, setWorkOrderSectionOrChapter] = useState<IListItem<string>>();
    const [constructionType, setConstructionType] = useState<ConstructionTypeData>();
    const [selectedActKc2Other, setSelectedActKc2Other] = useState<BimActKc2OtherItemData | BimActKc2OtherData>();
    const [priceLevel, setPriceLevel] = useState<BimPriceLevelEnum>();

    const bimChapter = useMemo(
        () =>
            isBimChapter(workOrderSectionOrChapter?.id) ? (workOrderSectionOrChapter?.id as BimChapterEnum) : undefined,
        [workOrderSectionOrChapter?.id],
    );
    const bimOtherChapter = useMemo(
        () =>
            isOtherBimChapter(workOrderSectionOrChapter?.id)
                ? (workOrderSectionOrChapter?.id as BimChapterEnum)
                : undefined,
        [workOrderSectionOrChapter?.id],
    );
    const bimTotalsChapter = useMemo(
        () => (isTotalsBimChapter(workOrderSectionOrChapter?.id) ? workOrderSectionOrChapter?.id : undefined),
        [workOrderSectionOrChapter?.id],
    );

    const { isBimPresenceActs } = useBimActsKC2Other(
        workOrderId,
        workOrderRootObject?.workOrderObjectId,
        !!bimOtherChapter,
    );

    const [totalChapterDetailsRowOpenOuter, setTotalChapterDetailsRowOpenOuter] = useState<{
        rowType: BimWorkOrderRowTypeEnum.TotalsString;
        data: null;
    } | null>(null);

    const toggleTotalChapterDetailsOuter = useCallback(() => {
        setTotalChapterDetailsRowOpenOuter((prev) =>
            prev
                ? null
                : {
                      data: null,
                      rowType: BimWorkOrderRowTypeEnum.TotalsString,
                  },
        );
    }, []);

    useEffect(() => {
        if (state?.openEditRequisites) {
            refreshProject();
            requisitesRefBtn.current?.openModalFromOutside();
        }
    }, [refreshProject, state?.openEditRequisites]);

    useEffect(() => {
        if (isObjectsFormationStage && isFrameContract) {
            navigate(`/projects/bim/${projectId}/order/${orderId}/structure`);
        }
    }, [isFrameContract, isObjectsFormationStage, navigate, orderId, projectId]);

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [projectData?.name, projectData?.uniqueId, methodology, projectId],
    );

    const { data: bimWorkOrderObjects } = useBimWorkOrderObjects({
        workOrderId,
        requestKey: 'ForTypesAndObjects',
        getKeyValuesOnly: false,
    });

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: ({ savedValuesObj }) => {
                    const savedConstructionTypeId = savedValuesObj?.constructionTypeId;
                    const savedFilters = savedValuesObj?.[String(savedConstructionTypeId)];

                    const savedConstructionType = findAllConstructionTypesFromObjects({
                        workOrderChangeObjectsData:
                            bimWorkOrderObjects?.items as unknown as WorkOrderChangeObjectData[],
                    }).find((type) => type.id === savedConstructionTypeId);

                    if (savedConstructionType && !state?.actNavInfo) {
                        setConstructionType(savedConstructionType);
                    }
                    if (!state?.actNavInfo) {
                        setWorkOrderSectionOrChapter(savedFilters?.chapter ?? undefined);
                        setWorkOrderRootObject(savedFilters?.object ?? undefined);
                    }
                },
                withEmptyFilterSetter:
                    bimWorkOrderObjects?.items && prevOrderId === orderId
                        ? (setter: (params: { key: string | undefined; newState: any }) => void) => {
                              const constructionTypes = findAllConstructionTypesFromObjects({
                                  workOrderChangeObjectsData:
                                      bimWorkOrderObjects?.items as unknown as WorkOrderChangeObjectData[],
                              });
                              const [firstConstructionType] = constructionTypes;

                              setConstructionType(firstConstructionType);
                              setter({
                                  key: 'constructionTypeId',
                                  newState: firstConstructionType?.id,
                              });
                          }
                        : undefined,
            }),
            [bimWorkOrderObjects?.items, orderId, prevOrderId, state?.actNavInfo],
        ),
    );

    const handleConstructionTypeChange = useCallback(
        (newConstructionType: ConstructionTypeData) => {
            setConstructionType(newConstructionType);

            if (!state?.actNavInfo) {
                setTimeout(() => {
                    setWorkOrderSectionOrChapter(savedFilterState?.[newConstructionType?.id]?.chapter ?? undefined);
                    setWorkOrderRootObject(savedFilterState?.[newConstructionType?.id]?.object ?? undefined);
                }, 0);
            }

            handleSaveFilterState({
                key: 'constructionTypeId',
                newState: newConstructionType.id,
            });
        },
        [handleSaveFilterState, savedFilterState, state?.actNavInfo],
    );

    const onRootObjectChange = useCallback(
        (rootObject: IListItem<number> | undefined) => {
            setWorkOrderRootObject(rootObject);
            if (!state?.actNavInfo) {
                handleSaveFilterState({
                    rootKey: String(constructionType?.id),
                    key: 'object',
                    newState: rootObject,
                });
            }
        },
        [constructionType?.id, handleSaveFilterState, state?.actNavInfo],
    );

    const onSectionOrChapterChange = useCallback(
        (sectionOrChapter: IListItem<string>) => {
            setWorkOrderSectionOrChapter(sectionOrChapter);

            const isTotalsChapter =
                sectionOrChapter?.id === totalsByConstructionTypeKey ||
                sectionOrChapter?.id === SectionEnum.WorkOrderTotals;

            if (isTotalsChapter || !sectionOrChapter) {
                setWorkOrderRootObject(undefined);
                handleSaveFilterState({
                    key: String(constructionType?.id),
                    newState: {
                        object: undefined,
                        chapter: sectionOrChapter,
                    },
                });
            } else if (!state?.actNavInfo) {
                handleSaveFilterState({
                    rootKey: String(constructionType?.id),
                    key: 'chapter',
                    newState: sectionOrChapter,
                });
            }
        },
        [constructionType?.id, handleSaveFilterState, state?.actNavInfo],
    );

    const clickOpenRef = useRef<any>();

    const [isOpenModalOtherEdit, setIsOpenModalOtherEdit] = useState(false);
    const [position, setPosition] = useState<number | undefined>();

    useEffect(() => {
        if (workOrderFilterRef.current) {
            setPosition(workOrderFilterRef.current?.getBoundingClientRect().bottom);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workOrderFilterRef, isHeaderOpened, workOrderFilterRef.current?.getBoundingClientRect().bottom]);

    const onClose = useCallback(() => {
        setSelectedActKc2Other(undefined);
        setIsOpenModalOtherEdit(false);
    }, []);

    const onOpenActKc2Edit = useCallback((act: BimActKc2OtherItemData | BimActKc2OtherData) => {
        setSelectedActKc2Other(act);
        setIsOpenModalOtherEdit(true);
    }, []);

    const togglePopover = useCallback(() => {
        setIsPopoverVisible((isOpen) => !isOpen);
    }, []);

    const onPopoverClickOutside = useCallback(() => {
        // TODO: удалить, когда в модалках и ошибками не будут всплывать собития клика
        if (!(requestData || requestError)) {
            togglePopover();
        }
    }, [requestData, requestError, togglePopover]);

    const onRequisitesEditSuccess = useCallback(() => {
        refreshProject();
        resetCheckedRowsRef?.current?.();
    }, [refreshProject]);

    const { priceListCounter, chaptersTooltip, makeRequestPriceListData } = useBimWorkOrderPriceCounter({
        workOrderId,
        workOrderSectionOrChapter,
        constructionTypeId: constructionType?.id,
        workOrderRootObjectId: workOrderRootObject?.workOrderObjectId,
    });

    const [resetFilterData] = useResetMultipleStoreData({
        requestIds: ['bimWorkOrderPriceCodeFilter', 'bimWorkOrderPriceNameFilter', 'bimWorkOrderPriceObjectNameFilter'],
    });

    const { makeRefreshDetailsData } = useBimWorkOrderTotalsRow({
        chapter: bimChapter,
        workOrderId,
        workOrderObjectId: workOrderRootObject?.workOrderObjectId,
        constructionTypeId: constructionType?.id,
        withEffect: false,
        resetFilterData,
        priceLevel,
    });

    const makeRefreshCurrentTable = useCallback(() => {
        if (priceTableApiRef?.current) {
            priceTableApiRef?.current?.api?.refreshInfiniteCache();

            if (!bimOtherChapter) {
                makeRefreshDetailsData();
                makeRequestPriceListData();
            } else {
                setSelectedActKc2Other(undefined);
            }
        }
    }, [bimOtherChapter, makeRefreshDetailsData, makeRequestPriceListData]);

    useEffect(
        () => () => {
            resetFilterData();
        },
        [resetFilterData],
    );

    const isShowPriceTablePossible = !!((workOrderRootObject && (bimChapter || bimOtherChapter)) || bimTotalsChapter);

    const isContractStatusDelayed = contractStatus === ContractStatusEnum.Delayed;
    const isContractStatusCompleted = contractStatus === ContractStatusEnum.Completed;

    const isWorkOrderStatusDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
    const isWorkOrderStatusCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;
    const isWorkOrderStatusOnFormation = workOrderStatus === WorkOrderStatusEnum.OnFormation;

    const isSectionAndObject = !workOrderSectionOrChapter || !workOrderRootObject || !workOrderId;

    const isDisabledButtonAdd =
        isSectionAndObject ||
        isWorkOrderStatusDelayed ||
        isWorkOrderStatusCompleted ||
        isContractStatusDelayed ||
        isContractStatusCompleted ||
        !orderRules?.canAddRecords;

    const isHiddenTooltipButtonAdd =
        !isSectionAndObject ||
        isWorkOrderStatusDelayed ||
        isWorkOrderStatusCompleted ||
        isContractStatusDelayed ||
        isContractStatusCompleted;

    const variables = useMemo(
        () => ({
            workOrderId,
        }),
        [workOrderId],
    );

    const [bimWorkOrderVersionsData, , , bimWorkOrderVersionsRequest] = useRequest({
        requestId: 'bimWorkOrderVersions',
        query: bimWorkOrderVersionsGql,
        variables,
        withEffect: false,
    });

    useEffect(() => {
        dispatch(bimWorkOrderVersionsRequest({ workOrderId }));
    }, [dispatch, workOrderId, bimWorkOrderVersionsRequest]);

    useBimScrollToAct({
        bimWorkOrderObjects: bimWorkOrderObjects?.items as BimWorkOrderObjectData[],
        onRootObjectChange,
        priceTableApiRef,
        onSectionOrChapterChange,
        handleConstructionTypeChange,
    });

    return (
        <ProjectPageLayout
            layoutEntityKey={workOrderId}
            title={workOrderTitle}
            statusWorkOrder={workOrderStatus}
            breadCrumbsItems={breadCrumbsItems}
            isWorkOrderChangeRequired={isWorkOrderChangeRequired}
            isHeaderCollapsable
            headerBottom={
                <Toolbar flex>
                    <BimOrderRequisites
                        ref={requisitesRefBtn}
                        isFrameContract={isFrameContract}
                        orderId={workOrderId}
                        onSuccess={onRequisitesEditSuccess}
                        isLoading={isPageDataLoading}
                        workOrderStatus={workOrderStatus}
                        contractStatus={contractStatus}
                        orderRules={orderRules}
                        statusProject={statusProject}
                        bimWorkOrderVersionsRequest={bimWorkOrderVersionsRequest}
                        workOrderId={workOrderId}
                        projectDataStatus={projectDataStatus}
                    />
                    {!!bimWorkOrderVersionsData?.bimWorkOrderVersions?.length && !isWorkOrderStatusOnFormation && (
                        <SelectVersionsWorkOrder items={bimWorkOrderVersionsData?.bimWorkOrderVersions} />
                    )}
                    <BimWorkOrderKebabMenu
                        statusWorkOrder={workOrderStatus}
                        statusProject={statusProject}
                        statusContract={contractStatus}
                        hasActsKC2={hasActsKC2}
                        hasWorkOrderChangesValidStatus={hasWorkOrderChangesValidStatus}
                        isFrameContract={isFrameContract}
                        orderRules={orderRules}
                        hasInProgress={hasInProgress}
                        projectData={projectData}
                        refreshProjectData={refreshProject}
                        isWorkOrderChangeRequired={isWorkOrderChangeRequired}
                    />
                    {!bimOtherChapter && <BimPriceLevelChoice priceLevel={priceLevel} setPriceLevel={setPriceLevel} />}
                </Toolbar>
            }
            style={{ marginBottom: 0 }}
        >
            <WorkOrderConstructionTypesFromObjects
                onChange={handleConstructionTypeChange}
                workOrderChangeObjectsData={bimWorkOrderObjects?.items as unknown as WorkOrderChangeObjectData[]}
                activeConstructionTypeId={constructionType?.id}
            />

            <BimWorkOrderFilterWithTools
                ref={workOrderFilterRef}
                onRootObjectChange={onRootObjectChange}
                onSectionOrChapterChange={onSectionOrChapterChange}
                sectionOrChapter={workOrderSectionOrChapter}
                rootObject={workOrderRootObject}
                objectsListItems={bimWorkOrderObjects?.items}
                constructionTypeId={constructionType?.id}
                isFrameContract={isFrameContract}
            >
                <Layout>
                    <BimWorkOrderPriceCounter priceListCounter={priceListCounter} chaptersTooltip={chaptersTooltip} />
                    <TooltipWrapper
                        isHidden={isHiddenTooltipButtonAdd}
                        direction="upCenter"
                        message="Выберите раздел и объект"
                    >
                        <Button
                            className={isPopoverVisible ? 'open' : ''}
                            size="s"
                            iconRight={IconArrowDown}
                            label="Добавить"
                            onClick={togglePopover}
                            ref={buttonAddedRef}
                            disabled={isDisabledButtonAdd}
                        />
                    </TooltipWrapper>
                    {isPopoverVisible && (
                        <Popover
                            direction="downCenter"
                            spareDirection="downCenter"
                            offset="2xs"
                            arrowOffset={0}
                            onClickOutside={onPopoverClickOutside}
                            isInteractive
                            equalAnchorWidth
                            anchorRef={buttonAddedRef}
                        >
                            <div className="popoverContent">
                                <BimUploadPriceFiles
                                    workOrderId={workOrderId}
                                    selectedChapterOrSection={workOrderSectionOrChapter}
                                    selectedObject={workOrderRootObject}
                                    onUploadSuccess={makeRefreshCurrentTable}
                                    workOrderStatus={workOrderStatus}
                                    isFrameContract={isFrameContract}
                                    togglePopover={togglePopover}
                                    isOther={!!bimOtherChapter}
                                    statusProject={statusProject}
                                />
                                <BimAddOrderOther
                                    isDisabled={!bimOtherChapter}
                                    constructionTypeId={constructionType?.id}
                                    bimOtherChapter={bimOtherChapter}
                                    workOrderRootObject={workOrderRootObject}
                                    workOrderChangeObjectsData={
                                        bimWorkOrderObjects?.items as unknown as WorkOrderChangeObjectData[]
                                    }
                                    isFrameContract={isFrameContract}
                                    onSuccess={makeRefreshCurrentTable}
                                    workOrderStatus={workOrderStatus}
                                    hasOtherActsKc2={hasOtherActsKc2}
                                    isBimPresenceActs={isBimPresenceActs}
                                    togglePopover={togglePopover}
                                    statusProject={statusProject}
                                />
                                <BimAddKC2
                                    priceTableApiRef={priceTableApiRef}
                                    workOrderId={workOrderId}
                                    selectedChapterOrSection={workOrderSectionOrChapter}
                                    selectedObject={workOrderRootObject}
                                    onUploadClose={makeRefreshCurrentTable}
                                    isFrameContract={isFrameContract}
                                    workOrderStatus={workOrderStatus}
                                    constructionType={constructionType}
                                    clickOpenRef={clickOpenRef}
                                    refreshProject={refreshProject}
                                    togglePopover={togglePopover}
                                    statusProject={statusProject}
                                    isDisabledCreateKC2OtherRules={!aktKC2BimOtherRules.canCreate}
                                    isDisabledCreateAktKC2BimRules={!aktKC2BimRules.canCreate}
                                />
                            </div>
                        </Popover>
                    )}
                    {isOpenModalOtherEdit && !selectedActKc2Other?.isCorrection && !!bimOtherChapter && (
                        <BimActKc2OtherEdit
                            actKC2Other={selectedActKc2Other as BimActKc2OtherItemData}
                            onClose={onClose}
                            workOrderId={workOrderId}
                            constructionType={constructionType}
                            selectedObject={workOrderRootObject}
                            priceTableApiRef={priceTableApiRef}
                            refreshProject={refreshProject}
                            isDisabled={isContractStatusDelayed || isContractStatusCompleted}
                        />
                    )}
                    {isOpenModalOtherEdit && selectedActKc2Other?.isCorrection && !!bimOtherChapter && (
                        <BimEditActKC2OtherCorrection
                            actKc2OtherId={selectedActKc2Other?.id}
                            onClose={onClose}
                            workOrderId={workOrderId}
                            constructionType={constructionType}
                            selectedObject={workOrderRootObject}
                            refreshProject={refreshProject}
                            makeRefreshCurrentTable={makeRefreshCurrentTable}
                        />
                    )}
                </Layout>
            </BimWorkOrderFilterWithTools>
            {isShowPriceTablePossible ? (
                <div
                    id="bimWorkOrderContainer"
                    style={
                        {
                            '--tableStartPosition': isNumber(position) && `${position ?? 0}px`,
                        } as React.CSSProperties
                    }
                >
                    {bimChapter && (
                        <BimWorkOrderPriceTable
                            workOrderId={workOrderId}
                            constructionTypeId={constructionType?.id}
                            workOrderRootObjectId={workOrderRootObject?.workOrderObjectId}
                            chapter={bimChapter}
                            priceLevel={priceLevel}
                            totalChapterDetailsRowOpenOuter={totalChapterDetailsRowOpenOuter}
                            toggleTotalChapterDetailsOuter={toggleTotalChapterDetailsOuter}
                            makeRefreshTable={makeRefreshCurrentTable}
                            ref={priceTableApiRef}
                            workOrderStatus={workOrderStatus}
                            resetCheckedRowsRef={resetCheckedRowsRef}
                            contractStatus={contractStatus}
                            statusProject={statusProject}
                            orderRules={orderRules}
                            hasInProgress={hasInProgress}
                            isFrameContract={isFrameContract}
                            refreshProject={refreshProject}
                        />
                    )}

                    {bimTotalsChapter && (
                        <BimWorkOrderTotalsTableContainer
                            workOrderId={workOrderId}
                            constructionTypeId={constructionType?.id}
                            bimTotalsChapter={bimTotalsChapter}
                            workOrderStatus={workOrderStatus}
                            ref={priceTableApiRef}
                            hasInProgress={hasInProgress}
                            isFrameContract={isFrameContract}
                            priceLevel={priceLevel}
                        />
                    )}

                    {bimOtherChapter && (
                        <BimWorkOrderOthersTable
                            workOrderId={workOrderId}
                            statusWorkOrder={workOrderStatus}
                            hasInProgress={hasInProgress}
                            constructionTypeId={constructionType?.id}
                            workOrderRootObjectId={workOrderRootObject?.workOrderObjectId}
                            totalDetailsRowOpenOuter={!!totalChapterDetailsRowOpenOuter}
                            toggleTotalDetailsOuter={toggleTotalChapterDetailsOuter}
                            ref={priceTableApiRef}
                            onOpenActKc2Edit={onOpenActKc2Edit}
                            isFrameContract={isFrameContract}
                            refreshProject={refreshProject}
                            resetCheckedRowsRef={resetCheckedRowsRef}
                            contractStatus={contractStatus}
                            orderRules={orderRules}
                            statusProject={statusProject}
                        />
                    )}
                </div>
            ) : (
                <ChooseSectionText className="choiceMessage" text="Для отображения данных выберите раздел и объект" />
            )}
        </ProjectPageLayout>
    );
};

BimWorkOrder.displayName = 'BimWorkOrder';
